import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Input, message, Tooltip } from "antd";
import { ModalEditConfirm } from "../../Utils/UI/Text";
import "./DoubleClickEditLabel.css";
import { patchDoubleClickEditLabel } from "../Infrastructure/DoubleClickEditLabel.service";

const DoubleClickEditLabel = ({
  str,
  labelKey,
  type,
  id,
  onRefresh,
  id_resource,
  confirmBeforeEdit = true,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(str);
  const [originalValue, setOriginalValue] = useState(str);
  const [loading, setLoading] = useState(false);
  const isSendingRef = useRef(false);

  useEffect(() => {
    setValue(str);
    setOriginalValue(str);
  }, [str]);

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleChange = (e) => {
    setValue(e.target.value);
  };

  const sendEdit = async () => {
    if (isSendingRef.current) return;
    isSendingRef.current = true;

    if (value === originalValue) {
      setIsEditing(false);
      isSendingRef.current = false;
      return;
    }

    const performEdit = async () => {
      setLoading(true);
      try {
        const data = [
          {
            path: `/${labelKey}`,
            op: "replace",
            value: value,
          },
        ];

        await patchDoubleClickEditLabel({ type, id, id_resource }, data);

        if (onRefresh) await onRefresh();
        message.success("Datos editados con éxito.");
      } catch (error) {
        message.error("Se produjo un error, vuelve a intentarlo");
      } finally {
        setIsEditing(false);
        setLoading(false);
        isSendingRef.current = false;
      }
    };

    if (confirmBeforeEdit) {
      ModalEditConfirm({
        title: "¿Estás seguro de que deseas editar este dato?",
        messageOnOk: "Datos editados con éxito.",
        onConfirm: performEdit,
        onCancel: () => {
          setValue(originalValue);
          setIsEditing(false);
          isSendingRef.current = false;
          message.info("Edición cancelada.");
        },
      });
    } else {
      await performEdit();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      sendEdit();
    }
  };

  const handleBlur = () => {
    sendEdit();
  };

  return (
    <div
      onDoubleClick={handleDoubleClick}
      style={{ cursor: "pointer", minWidth: "200px" }}
    >
      {isEditing ? (
        <Input
          type={type}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          onFocus={(e) => e.target.select()}
          disabled={loading}
          style={{
            padding: "4px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            width: "100%",
          }}
        />
      ) : (
        <Tooltip title="Doble clic para editar. Presiona Enter para guardar.">
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.2s",
            }}
            className="editable-label"
          >
            {value}
          </span>
        </Tooltip>
      )}
    </div>
  );
};

DoubleClickEditLabel.propTypes = {
  str: PropTypes.string.isRequired,
  labelKey: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  id_resource: PropTypes.string,
  onRefresh: PropTypes.func,
  confirmBeforeEdit: PropTypes.bool,
};

export default DoubleClickEditLabel;
